import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Alert,
  ActivityIndicator,
  RefreshControl,
  Animated,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { colors } from '../constants/colors';
import { GitHubTimetableService, TimetableInfo } from '../services/GitHubTimetableService';
import { importTimetableFromICS } from '../services/ICSService';
import { clearAllNotifications } from '../services/notifications';

// Define colors for different courses
const COURSE_COLORS = [
  '#4CAF50',  // Green
  '#2196F3',  // Blue
  '#9C27B0',  // Purple
  '#FF9800',  // Orange
  '#E91E63',  // Pink
];

export default function CourseSelectionScreen({ navigation }: any) {
  const [loading, setLoading] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<string | null>(null);
  const [timetables, setTimetables] = useState<TimetableInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedFaculties, setExpandedFaculties] = useState<Record<string, boolean>>({});

  const loadTimetables = useCallback(async (forceRefresh = false) => {
    try {
      setError(null);
      if (forceRefresh) {
        await GitHubTimetableService.clearCache();
      }
      const data = await GitHubTimetableService.getFaculties();
      setTimetables(data);
    } catch (err) {
      console.error('Error loading timetables:', err);
      setError('Failed to load timetables. Please check your internet connection.');
    }
  }, []);

  const onRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await loadTimetables(true);
    } finally {
      setIsRefreshing(false);
    }
  }, [loadTimetables]);

  useEffect(() => {
    const initialLoad = async () => {
      setIsLoading(true);
      try {
        await loadTimetables();
      } finally {
        setIsLoading(false);
      }
    };
    initialLoad();
  }, [loadTimetables]);

  const handleCourseSelect = async (timetable: TimetableInfo) => {
    try {
      setSelectedCourse(timetable.id);
      setLoading(true);
      
      // Clear existing notifications before importing new timetable
      await clearAllNotifications();

      // Fetch and import the timetable
      const icsContent = await GitHubTimetableService.fetchTimetable(timetable.id);
      await importTimetableFromICS(icsContent);
      
      // Save selected course
      await AsyncStorage.setItem('@selected_course', timetable.id);
      
      // Show success message
      Alert.alert(
        'Success',
        `${timetable.name} timetable imported successfully!`,
        [
          {
            text: 'OK',
            onPress: () => {
              navigation.reset({
                index: 0,
                routes: [{ name: 'Main' }],
              });
            },
          },
        ],
      );
    } catch (error) {
      console.error('Error importing timetable:', error);
      Alert.alert(
        'Error',
        'Failed to import timetable. Please check your internet connection and try again.'
      );
      setSelectedCourse(null);
    } finally {
      setLoading(false);
    }
  };

  const toggleFaculty = (faculty: string) => {
    setExpandedFaculties(prev => ({
      ...prev,
      [faculty]: !prev[faculty]
    }));
  };

  // Group timetables by faculty
  const timetablesByFaculty = timetables.reduce((acc, timetable) => {
    if (!acc[timetable.faculty]) {
      acc[timetable.faculty] = [];
    }
    acc[timetable.faculty].push(timetable);
    return acc;
  }, {} as Record<string, TimetableInfo[]>);

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Loading timetables...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>
        <View style={styles.headerText}>
          <Text style={styles.title}>Select Your Course</Text>
          <Text style={styles.subtitle}>
            Choose your course to load your class schedule
          </Text>
        </View>
      </View>

      <ScrollView 
        style={styles.content}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={onRefresh}
            colors={[colors.primary]}
            tintColor={colors.primary}
          />
        }
      >
        {error ? (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>{error}</Text>
            <TouchableOpacity style={styles.retryButton} onPress={() => loadTimetables(true)}>
              <Text style={styles.retryButtonText}>Retry</Text>
            </TouchableOpacity>
          </View>
        ) : Object.entries(timetablesByFaculty).length === 0 ? (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>No timetables available</Text>
            <Text style={styles.emptySubtext}>Pull down to refresh</Text>
          </View>
        ) : (
          <View style={styles.timetablesContainer}>
            {Object.entries(timetablesByFaculty).map(([faculty, facultyTimetables]) => (
              <View key={faculty} style={styles.facultySection}>
                <TouchableOpacity
                  style={styles.facultyHeader}
                  onPress={() => toggleFaculty(faculty)}
                >
                  <View style={styles.facultyHeaderContent}>
                    <Ionicons name="school" size={24} color={colors.primary} />
                    <Text style={styles.facultyTitle}>{faculty}</Text>
                  </View>
                  <Ionicons 
                    name={expandedFaculties[faculty] ? "chevron-up" : "chevron-down"} 
                    size={24} 
                    color={colors.textSecondary} 
                  />
                </TouchableOpacity>
                
                {expandedFaculties[faculty] && (
                  <View style={styles.timetablesList}>
                    {facultyTimetables.map((timetable, index) => {
                      const color = COURSE_COLORS[index % COURSE_COLORS.length];
                      return (
                        <TouchableOpacity
                          key={timetable.id}
                          style={[styles.courseCard, { borderLeftColor: color }]}
                          onPress={() => handleCourseSelect(timetable)}
                          disabled={loading}
                        >
                          <View style={[styles.iconContainer, { backgroundColor: color + '20' }]}>
                            <Ionicons 
                              name="school-outline" 
                              size={32} 
                              color={color} 
                            />
                          </View>
                          <View style={styles.courseInfo}>
                            <Text style={styles.courseName}>{timetable.name}</Text>
                          </View>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                )}
              </View>
            ))}
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  header: {
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButton: {
    marginRight: 16,
  },
  headerText: {
    flex: 1,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    color: colors.textSecondary,
  },
  content: {
    flex: 1,
  },
  timetablesContainer: {
    padding: 16,
  },
  facultySection: {
    marginBottom: 16,
    backgroundColor: colors.surface,
    borderRadius: 12,
    overflow: 'hidden',
  },
  facultyHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    backgroundColor: colors.surface,
  },
  facultyHeaderContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  facultyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.text,
    marginLeft: 12,
  },
  timetablesList: {
    padding: 16,
    paddingTop: 0,
  },
  courseCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.cardBackground,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderLeftWidth: 4,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  courseInfo: {
    flex: 1,
  },
  courseName: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 4,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: colors.textSecondary,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorText: {
    fontSize: 16,
    color: colors.error,
    textAlign: 'center',
    marginBottom: 16,
  },
  retryButton: {
    backgroundColor: colors.primary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  retryButtonText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '600',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    marginTop: 40,
  },
  emptyText: {
    fontSize: 18,
    color: colors.text,
    marginBottom: 8,
    textAlign: 'center',
  },
  emptySubtext: {
    fontSize: 14,
    color: colors.textSecondary,
    textAlign: 'center',
  },
});
